import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  orderBy,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db, functions, auth } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import BehavioralLogsService from './BehavioralLogsService';
import MonitoringService from './MonitoringService';
import { enqueueXpCall } from '../utils/xpCallQueue';

/**
 * Service pour gérer les opérations Firestore liées aux utilisateurs
 */
class UserService {

  /**
   * Créer un nouveau profil utilisateur dans Firestore
   * @param {string} userId - ID de l'utilisateur Firebase Auth
   * @param {string} email - Email de l'utilisateur
   * @param {string} pseudo - Pseudo choisi par l'utilisateur
   * @param {number} age - Âge de l'utilisateur
   * @param {number} drumYears - Années de pratique batterie (1, 2, 3, 4, 5, 6+)
   * @param {string} accountType - Type de compte ('anonymous', 'student', 'email')
   */
  async createUserProfile(userId, email, pseudo, age = null, drumYears = null, accountType = 'email') {
    try {
      const userRef = doc(db, 'users', userId);

      const userData = {
        profile: {
          pseudo: pseudo,
          // email retiré du profil Firestore public — utiliser Firebase Auth currentUser.email
          age: age,
          drumYears: drumYears,
          accountType: accountType, // 'anonymous', 'student', 'email'
          avatar: '/images/avatars/image17.png', // Avatar par défaut
          role: 'student', // 'student' ou 'teacher'
          teacherId: null, // ID du professeur (si élève rattaché)
          teacherCode: null, // Code unique généré si role = 'teacher'
          notation: 'europe', // Nomenclature par défaut ('cyril', 'europe', 'world')
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          isFirstLogin: false
        },
        stats: {
          totalXP: 0,
          exercisesCompleted: 0,
          totalPracticeTime: 0 // en minutes
        },
        streakData: {
          currentStreak: 0,
          lastPracticeDate: null,
          weeklyPracticeMinutes: [] // 7 derniers jours
        },
        midiConfig: {
          // Mapping MIDI par défaut (General MIDI standard)
          mapping: {
            'D4': 36,   // Grosse caisse
            'B4': 38,   // Caisse claire
            'G5': 22,   // Hi-hat fermé
            'G#5': 26,  // Hi-hat ouvert
            'E5': 48,   // Tom 1
            'D5': 45,   // Tom 2
            'F4': 43,   // Tom 3
            'F5': 51,   // Cymbale Ride
            'A5': 49    // Cymbale Crash
          }
        },
        velocityStats: {
          // Statistiques de vélocité par instrument
          kick: { count: 0, sum: 0, recent: [] },   // Grosse caisse
          snare: { count: 0, sum: 0, recent: [] },  // Caisse claire
          hihat: { count: 0, sum: 0, recent: [] }   // Charleston
        }
      };

      await setDoc(userRef, userData);
      return userData;
    } catch (error) {
      console.error('❌ Erreur création profil utilisateur:', error);
      throw error;
    }
  }

  /**
   * Récupérer le profil complet d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   */
  async getUserProfile(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        return userSnap.data();
      } else {
        return null;
      }
    } catch (error) {
      console.error('❌ Erreur récupération profil:', error);
      throw error;
    }
  }

  /**
   * Alias de getUserProfile pour compatibilité
   * @param {string} userId - ID de l'utilisateur
   */
  async getProfile(userId) {
    return this.getUserProfile(userId);
  }

  /**
   * Vérifier si c'est la première connexion de l'utilisateur
   * @param {string} userId - ID de l'utilisateur
   */
  async isFirstLogin(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      // Si le document n'existe pas, c'est la première connexion
      if (!userSnap.exists()) {
        return true;
      }

      // Sinon, vérifier le flag isFirstLogin
      const userData = userSnap.data();
      return userData.profile?.isFirstLogin !== false;
    } catch (error) {
      console.error('❌ Erreur vérification première connexion:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le pseudo d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} newPseudo - Nouveau pseudo
   */
  async updatePseudo(userId, newPseudo) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'profile.pseudo': newPseudo
      });
    } catch (error) {
      console.error('❌ Erreur mise à jour pseudo:', error);
      throw error;
    }
  }

  /**
   * Marque un badge de challenge (profile.challengeBadges) comme vu, après affichage
   * de la notification de gain d'XP. Lecture-modification-écriture transactionnelle
   * pour éviter d'écraser un badge ajouté entre-temps par une autre clôture de période.
   * @param {string} userId
   * @param {string} challengeId - periodId du challenge (ex: "drill-2026-06" ou "2026-06")
   * @param {number} position - 1, 2 ou 3
   */
  async markChallengeBadgeSeen(userId, challengeId, position) {
    try {
      const userRef = doc(db, 'users', userId);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        const data = snap.data() || {};
        const badges = data.profile?.challengeBadges || [];
        const updated = badges.map(b =>
          (b.challengeId === challengeId && b.position === position)
            ? { ...b, seen: true }
            : b
        );
        transaction.update(userRef, { 'profile.challengeBadges': updated });
      });
    } catch (error) {
      console.error('❌ Erreur marquage badge vu:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour l'avatar d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} avatarPath - Chemin de l'avatar (ex: '/images/avatars/avatar1.png')
   */
  async updateAvatar(userId, avatarPath) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'profile.avatar': avatarPath
      });
      console.log('✅ Avatar mis à jour:', avatarPath);
    } catch (error) {
      console.error('❌ Erreur mise à jour avatar:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour la date de dernière connexion
   * @param {string} userId - ID de l'utilisateur
   */
  async updateLastLogin(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'profile.lastLogin': serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Erreur mise à jour lastLogin:', error);
      throw error;
    }
  }

  /**
   * Enregistrer la progression d'un exercice
   * @param {string} userId - ID de l'utilisateur
   * @param {string} exerciseId - ID de l'exercice
   * @param {number} score - Score obtenu (0-100)
   * @param {number} totalErrors - Nombre total d'erreurs
   * @param {number} xpEarned - XP gagnés pour cette tentative
   */
  async saveExerciseProgress(userId, exerciseId, score, totalErrors, xpEarned, bonusXP = 0) {
    try {
      const fn = httpsCallable(functions, 'saveExerciseProgress');
      const currentUserId = auth.currentUser?.uid;
      const payload = { exerciseId, score, totalErrors, xpEarned };
      if (bonusXP > 0) payload.bonusXP = bonusXP;
      if (userId && userId !== currentUserId) payload.targetUserId = userId;
      // File partagée avec addXP : les deux CF utilisent le même rate-limit serveur de 3 s
      const result = await enqueueXpCall(() => fn(payload));
      const serverXP = result?.data?.xpEarned ?? 0;
      BehavioralLogsService.xpTransaction(userId, {
        exerciseId,
        score,
        // xpClient n'est plus calculé côté client depuis le passage à l'XP 100% serveur
        // (voir CF saveExerciseProgress) — xpEarned reçu ici est toujours 0, le logguer
        // reviendrait à comparer une valeur bidon à xpServer et fausser le monitoring.
        xpClient: null,
        xpServer: serverXP,
        source: 'standard',
      });
      return serverXP;
    } catch (error) {
      console.error('❌ Erreur sauvegarde progression:', error);
      throw error;
    }
  }

  /**
   * Attribuer des XP pour les activités non-exercices (quiz, mémorisation, roulette, etc.)
   * @param {number} xp - Nombre de XP à attribuer (0-50)
   * @param {string} source - Source autorisée : 'quiz', 'memorisation', 'lecture', 'breaks', 'roulette-bonus'
   */
  async addXP(xp, source, targetUserId = null) {
    // Calculé avant l'appel réseau : reste disponible dans le catch si la CF échoue,
    // pour que xp_save_failed soit corrélable à un utilisateur (userHash) au lieu de rester anonyme.
    const userId = targetUserId || auth.currentUser?.uid;
    try {
      const fn = httpsCallable(functions, 'addXP');
      // File partagée avec saveExerciseProgress : même rate-limit serveur de 3 s
      await enqueueXpCall(() => fn({ xp, source, ...(targetUserId ? { targetUserId } : {}) }));
      BehavioralLogsService.xpTransaction(userId, {
        exerciseId: null,
        score: null,
        xpClient: xp,
        xpServer: xp, // addXP ne recalcule pas
        source,
      });
    } catch (error) {
      console.error('❌ Erreur attribution XP:', error);
      MonitoringService.error('xp_save_failed', { errorMessage: error.message, source, xp }, userId);
      throw error;
    }
  }

  /**
   * Récupérer le classement mensuel (XP du mois en cours)
   * Le mois commence le 1er à minuit
   */
  async getRanking() {
    try {
      // Calculer le début du mois courant (1er à minuit)
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const periodStart = Timestamp.fromDate(monthStart);

      // Récupérer uniquement les vrais élèves (pas les anonymes ni comptes partiels)
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(
        query(usersRef, where('profile.accountType', 'in', ['student', 'email']))
      );

      const ranking = [];

      // Pour chaque utilisateur, sommer les XP du mois
      await Promise.all(usersSnap.docs.map(async (userDoc) => {
        const data = userDoc.data();
        const xpHistoryRef = collection(db, `users/${userDoc.id}/xpHistory`);
        const monthQuery = query(xpHistoryRef, where('timestamp', '>=', periodStart));
        const xpSnap = await getDocs(monthQuery);

        let monthlyXP = 0;
        xpSnap.forEach(d => { monthlyXP += d.data().xp || 0; });

        ranking.push({
          userId: userDoc.id,
          pseudo: data.profile?.pseudo || 'Anonyme',
          monthlyXP,
          totalXP: data.stats?.totalXP || 0,
          exercisesCompleted: data.stats?.exercisesCompleted || 0,
          lastLogin: data.profile?.lastLogin
        });
      }));

      // Trier par XP mensuel décroissant, puis totalXP en cas d'égalité
      ranking.sort((a, b) => b.monthlyXP - a.monthlyXP || b.totalXP - a.totalXP);

      // Ajouter la position (ne compter que les utilisateurs actifs cette semaine)
      ranking.forEach((user, index) => {
        user.position = index + 1;
      });

      return ranking;
    } catch (error) {
      console.error('❌ Erreur récupération ranking:', error);
      throw error;
    }
  }

  /**
   * Récupérer l'historique d'un exercice pour un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} exerciseId - ID de l'exercice
   */
  async getExerciseHistory(userId, exerciseId) {
    try {
      const exerciseRef = doc(db, `users/${userId}/exerciseHistory`, exerciseId);
      const exerciseSnap = await getDoc(exerciseRef);

      if (exerciseSnap.exists()) {
        return exerciseSnap.data();
      }
      return null;
    } catch (error) {
      console.error('❌ Erreur récupération historique exercice:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le mapping MIDI personnalisé
   * @param {string} userId - ID de l'utilisateur
   * @param {object} mapping - Nouveau mapping MIDI
   */
  async updateMidiMapping(userId, mapping) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'midiConfig.mapping': mapping
      });
    } catch (error) {
      console.error('❌ Erreur mise à jour mapping MIDI:', error);
      throw error;
    }
  }

  /**
   * Récupérer le mapping MIDI d'un utilisateur
   * @param {string} userId - ID de l'utilisateur
   */
  async getMidiMapping(userId) {
    try {
      const userData = await this.getUserProfile(userId);
      return userData?.midiConfig?.mapping || null;
    } catch (error) {
      console.error('❌ Erreur récupération mapping MIDI:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le temps de pratique
   * @param {string} userId - ID de l'utilisateur
   * @param {number} minutes - Minutes de pratique à ajouter
   */
  async addPracticeTime(userId, minutes) {
    try {
      const userRef = doc(db, 'users', userId);

      // Si le document n'existe pas, le créer d'abord
      const userData = await this.getUserProfile(userId);
      if (!userData) {
        console.log('⚠️ Document utilisateur inexistant, création automatique...');
        await this.createUserProfile(userId, { email: 'user@example.com' });
      }

      const now = new Date();
      const todayUTC = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate()
      ));

      // Transaction atomique : lecture + écriture de weeklyPracticeMinutes en une seule opération
      // Évite la race condition où deux appels simultanés lisent la même valeur stale
      // et s'écrasent mutuellement (totalPracticeTime était correct via increment,
      // mais weeklyPracticeMinutes perdait des minutes)
      let oldLastPracticeDate = null;
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        const data = snap.data() || {};

        oldLastPracticeDate = data.streakData?.lastPracticeDate?.toDate() || null;
        const lastPracticeDayUTC = oldLastPracticeDate
          ? new Date(Date.UTC(
              oldLastPracticeDate.getUTCFullYear(),
              oldLastPracticeDate.getUTCMonth(),
              oldLastPracticeDate.getUTCDate()
            ))
          : null;

        let weeklyMinutes = data.streakData?.weeklyPracticeMinutes || [];
        if (!Array.isArray(weeklyMinutes)) weeklyMinutes = [];

        if (!lastPracticeDayUTC || lastPracticeDayUTC.getTime() !== todayUTC.getTime()) {
          const daysDiff = lastPracticeDayUTC
            ? Math.floor((todayUTC - lastPracticeDayUTC) / (1000 * 60 * 60 * 24))
            : 7;
          for (let i = 0; i < daysDiff; i++) weeklyMinutes.push(0);
          weeklyMinutes = weeklyMinutes.slice(-7);
        }

        while (weeklyMinutes.length < 7) weeklyMinutes.unshift(0);
        weeklyMinutes[6] = (weeklyMinutes[6] || 0) + minutes;

        transaction.update(userRef, {
          'stats.totalPracticeTime': increment(minutes),
          'streakData.weeklyPracticeMinutes': weeklyMinutes,
          'streakData.lastPracticeDate': serverTimestamp()
        });
      });

      await this.updateStreak(userId, oldLastPracticeDate);
    } catch (error) {
      console.error('❌ Erreur ajout temps de pratique:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le streak (jours consécutifs)
   * @param {string} userId - ID de l'utilisateur
   * @param {Date|null} oldLastPracticeDate - Ancienne date de pratique (avant mise à jour)
   */
  async updateStreak(userId, oldLastPracticeDate = null) {
    try {
      const userData = await this.getUserProfile(userId);
      const now = new Date();

      // ⚠️ CORRECTION : Utiliser UTC pour éviter les décalages de fuseau horaire
      const todayUTC = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate()
      ));

      // ⚠️ FIX : Utiliser l'ancienne date passée en paramètre (avant mise à jour)
      // Si pas fournie, récupérer depuis userData (fallback pour rétrocompatibilité)
      const lastPractice = oldLastPracticeDate || userData.streakData?.lastPracticeDate?.toDate();
      const lastPracticeUTC = lastPractice
        ? new Date(Date.UTC(
            lastPractice.getUTCFullYear(),
            lastPractice.getUTCMonth(),
            lastPractice.getUTCDate()
          ))
        : null;

      let newStreak = 1;

      if (lastPracticeUTC) {
        const daysDiff = Math.floor((todayUTC - lastPracticeUTC) / (1000 * 60 * 60 * 24));

        if (daysDiff === 0) {
          // Même jour, pas de changement
          console.log('⏸️ Streak inchangé (même jour)');
          return;
        } else if (daysDiff === 1) {
          // Jour consécutif
          newStreak = (userData.streakData?.currentStreak || 0) + 1;
          console.log(`🔥 Streak augmenté: ${newStreak} jours consécutifs`);
        } else {
          // Streak cassé
          newStreak = 1;
          console.log(`💔 Streak cassé (${daysDiff} jours d'écart), retour à 1`);
        }
      } else {
        console.log('🆕 Premier jour de pratique, streak = 1');
      }

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'streakData.currentStreak': newStreak
        // Note: lastPracticeDate est déjà mis à jour par addPracticeTime()
      });

      return newStreak;
    } catch (error) {
      console.error('❌ Erreur mise à jour streak:', error);
      throw error;
    }
  }

  /**
   * Vérifier si un pseudo est déjà utilisé
   * @param {string} pseudo - Pseudo à vérifier
   * @returns {Promise<boolean>} - true si le pseudo existe déjà
   */
  async isPseudoTaken(pseudo) {
    try {
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);

      for (const doc of usersSnap.docs) {
        const data = doc.data();
        if (data.profile?.pseudo?.toLowerCase() === pseudo.toLowerCase()) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('❌ Erreur vérification pseudo:', error);
      throw error;
    }
  }

  /**
   * Générer un pseudo unique en ajoutant un suffixe si nécessaire
   * @param {string} basePseudo - Pseudo de base souhaité
   * @returns {Promise<string>} - Pseudo unique disponible
   */
  async generateUniquePseudo(basePseudo) {
    try {
      // Vérifier si le pseudo de base est disponible
      const isTaken = await this.isPseudoTaken(basePseudo);

      if (!isTaken) {
        return basePseudo;
      }

      // Si pris, chercher un suffixe disponible
      let suffix = 2;
      while (suffix < 100) { // Limite à 100 pour éviter boucle infinie
        const candidate = `${basePseudo}_${suffix}`;
        const candidateTaken = await this.isPseudoTaken(candidate);

        if (!candidateTaken) {
          return candidate;
        }

        suffix++;
      }

      // Si on arrive ici, générer un pseudo avec timestamp
      return `${basePseudo}_${Date.now().toString().slice(-4)}`;
    } catch (error) {
      console.error('❌ Erreur génération pseudo unique:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour l'âge et les années de batterie
   * @param {string} userId - ID de l'utilisateur
   * @param {number} age - Nouvel âge
   * @param {number} drumYears - Nouvelles années de pratique
   */
  async updateAgeAndDrumYears(userId, age, drumYears) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'profile.age': age,
        'profile.drumYears': drumYears
      });
    } catch (error) {
      console.error('❌ Erreur mise à jour âge/drumYears:', error);
      throw error;
    }
  }

  /**
   * Réinitialiser le temps de pratique et les données hebdomadaires
   * ATTENTION : Cette fonction efface complètement l'historique de pratique
   * @param {string} userId - ID de l'utilisateur
   */
  async resetPracticeTime(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'stats.totalPracticeTime': 0,
        'streakData.weeklyPracticeMinutes': [0, 0, 0, 0, 0, 0, 0],
        'streakData.lastPracticeDate': serverTimestamp()
      });
      console.log('✅ Temps de pratique réinitialisé pour l\'utilisateur:', userId);
    } catch (error) {
      console.error('❌ Erreur réinitialisation temps de pratique:', error);
      throw error;
    }
  }

  /**
   * Recalculer le streak à partir des données hebdomadaires existantes
   * Utile pour corriger les profils où updateStreak() n'était pas appelé
   * @param {string} userId - ID de l'utilisateur
   */
  async recalculateStreakFromWeekly(userId) {
    try {
      const userData = await this.getUserProfile(userId);
      const weeklyMinutes = userData.streakData?.weeklyPracticeMinutes || [];

      // Calculer le streak en partant d'aujourd'hui (index 6) et en remontant
      let streak = 0;
      for (let i = 6; i >= 0; i--) {
        if (weeklyMinutes[i] > 0) {
          streak++;
        } else {
          // Dès qu'on trouve un jour à 0, on arrête
          break;
        }
      }

      // Mettre à jour le streak
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'streakData.currentStreak': streak
      });

      console.log(`✅ Streak recalculé pour ${userId}: ${streak} jours`);
      return streak;
    } catch (error) {
      console.error('❌ Erreur recalcul streak:', error);
      throw error;
    }
  }

  /**
   * Révoquer un professeur et tous ses élèves liés
   * @param {string} teacherId - UID du professeur à révoquer
   * @returns {Promise<{revokedStudents: number}>}
   */
  async revokeTeacher(teacherId) {
    try {
      // 1. Révoquer le professeur
      const teacherRef = doc(db, 'users', teacherId);
      await updateDoc(teacherRef, {
        'profile.isRevoked': true,
        'profile.revokedAt': serverTimestamp()
      });

      // 2. Trouver et révoquer tous les élèves liés
      const usersRef = collection(db, 'users');
      const studentsSnap = await getDocs(
        query(usersRef, where('profile.teacherId', '==', teacherId))
      );

      let revokedStudents = 0;
      for (const studentDoc of studentsSnap.docs) {
        await updateDoc(studentDoc.ref, {
          'profile.isRevoked': true,
          'profile.revokedAt': serverTimestamp()
        });
        revokedStudents++;
      }

      // 3. Marquer le code d'accès comme révoqué
      const teacherData = (await getDoc(teacherRef)).data();
      const accessCode = teacherData?.profile?.accessCodeUsed;
      if (accessCode) {
        const codesRef = collection(db, 'teacherAccessCodes');
        const codeSnap = await getDocs(query(codesRef, where('code', '==', accessCode)));
        if (!codeSnap.empty) {
          await updateDoc(codeSnap.docs[0].ref, { status: 'revoked' });
        }
      }

      console.log(`✅ Professeur ${teacherId} révoqué, ${revokedStudents} élèves révoqués`);
      return { revokedStudents };
    } catch (error) {
      console.error('❌ Erreur révocation professeur:', error);
      throw error;
    }
  }

  /**
   * Rattacher un élève à un professeur via code
   * @param {string} studentId - ID de l'élève
   * @param {string} teacherCode - Code du professeur
   * @returns {Promise<object>} - Info du professeur
   */
  /**
   * Retire un élève de la liste du professeur connecté (CF removeStudent).
   * @returns {Promise<{removed: boolean, assignmentsDeleted: number}>} removed:false = déjà retiré (succès)
   */
  async removeStudentFromTeacher(studentId) {
    try {
      const removeStudent = httpsCallable(functions, 'removeStudent');
      const result = await removeStudent({ studentId });
      return result.data;
    } catch (error) {
      console.error('❌ Erreur retrait élève:', error);
      // Message générique volontaire : le serveur ne distingue pas "inconnu" de "autre prof"
      throw new Error('Impossible de retirer cet élève. Réessaie dans un instant.');
    }
  }

  async linkStudentToTeacher(studentId, teacherCode) {
    try {
      const joinTeacher = httpsCallable(functions, 'joinTeacher');
      const result = await joinTeacher({ teacherCode });
      console.log(`✅ Élève ${studentId} rattaché au professeur ${result.data.teacherId}`);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur rattachement élève:', error);
      if (error.code === 'functions/not-found') throw new Error('Code professeur invalide');
      if (error.code === 'functions/already-exists') throw new Error('Déjà rattaché à un autre professeur');
      throw error;
    }
  }

  /**
   * Récupérer la liste des élèves d'un professeur
   * @param {string} teacherId - ID du professeur
   * @returns {Promise<Array>} - Liste des élèves
   */
  async getTeacherStudents(teacherId) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('profile.teacherId', '==', teacherId));
      const usersSnap = await getDocs(q);

      const students = usersSnap.docs.map(userDoc => {
        const data = userDoc.data();
        return {
          userId: userDoc.id,
          pseudo: data.profile?.pseudo || 'Inconnu',
          age: data.profile?.age || 0,
          drumYears: data.profile?.drumYears || 0,
          totalXP: data.stats?.totalXP || 0,
          exercisesCompleted: data.stats?.exercisesCompleted || 0
        };
      });

      console.log(`✅ ${students.length} élèves trouvés pour prof ${teacherId}`);
      return students;
    } catch (error) {
      console.error('❌ Erreur récupération élèves:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour la nomenclature de l'utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {string} notation - Nomenclature choisie ('cyril', 'europe', 'world')
   */
  async updateNotation(userId, notation) {
    try {
      // Vérifier que la nomenclature est valide
      if (!['cyril', 'europe', 'world'].includes(notation)) {
        throw new Error(`Nomenclature invalide: ${notation}`);
      }

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'profile.notation': notation
      });

      console.log(`✅ Nomenclature mise à jour pour ${userId}: ${notation}`);
    } catch (error) {
      console.error('❌ Erreur mise à jour nomenclature:', error);
      throw error;
    }
  }

  /**
   * Récupérer la nomenclature de l'utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<string>} - Nomenclature ('cyril', 'europe', 'world')
   */
  async getNotation(userId) {
    try {
      const userData = await this.getUserProfile(userId);
      // Retourner 'europe' par défaut si non défini
      return userData?.profile?.notation || 'europe';
    } catch (error) {
      console.error('❌ Erreur récupération nomenclature:', error);
      return 'europe'; // Valeur par défaut en cas d'erreur
    }
  }
}

export default new UserService();
